import React from "react";
import { useLocation } from "react-router-dom";
import { AcxAgentService } from "services/AcxAgentService";

/**
 * The conversation ids behind a KBo Flow report finding. They arrive as navigation
 * state from the "View conversations" suggestion; a refresh or direct link re-derives
 * them from the chat. Undefined while that lookup is in flight.
 */
export function useFindingConversationIds(chatId: string, findingId: string) {
    const location = useLocation();
    // Captured once so the ids survive navigating into a conversation and back.
    const [conversationIds, setConversationIds] = React.useState(
        (location.state as { conversationIds?: string[] } | null)
            ?.conversationIds,
    );

    React.useEffect(() => {
        if (conversationIds) return;

        const abortController = new AbortController();
        new AcxAgentService()
            .getChatById({ chatId, abortSignal: abortController.signal })
            .then(
                (chat) =>
                    chat?.kboFlowFindings?.find(
                        (finding) => finding.findingId === findingId,
                    )?.conversationIds,
            )
            .catch(() => undefined)
            .then((ids) => {
                if (!abortController.signal.aborted) {
                    setConversationIds(ids ?? []);
                }
            });

        return () => abortController.abort();
    }, [conversationIds, chatId, findingId]);

    return conversationIds;
}
